import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getObjectDefinition } from '../content/registry/objectRegistry';
import { isStoreDirty, buildLiveMapDocument, useMapEditorStore } from './store/mapEditorStore';
import { ObjectBrowser } from '../tools/object-browser/ObjectBrowser';
import { ThumbnailRenderer } from './thumbnails/ThumbnailRenderer';
import { ObjectListSidebar, type SceneObjectRoomResolver } from './components/ObjectListSidebar';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal';
import { DuplicateOffsetPopover } from './components/DuplicateOffsetPopover';
import { PreSaveDiffModal } from './components/PreSaveDiffModal';
import { PrefabLibrary } from './components/PrefabLibrary';
import { getParamSchemas, validateInstance, type ParamIssue } from './validation/paramValidation';
import { useEngineRuntimeStore } from '../store/engineRuntimeStore';
import type {
  MapFloorLevel,
  MapObjectInstance,
} from '../content/schema/map';
import { lintMap, type LintIssue } from './validation/mapLinter';
import { AlignmentGuides } from './components/AlignmentGuides';

const AUTO_SAVE_DEBOUNCE_MS = 10_000;
const DELETE_CONFIRM_THRESHOLD = 3;

function formatNumber(value: number) {
  return value.toFixed(2);
}

function parseFieldNumber(value: string, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function getNudgeStep(): number {
  const state = useMapEditorStore.getState();
  if (state.snapMode === 'off') return 0.1;
  if (state.selectedObjectIds.length === 0) return 0.1;
  const firstId = state.selectedObjectIds[0];
  const instance = state.objectInstances.find((inst) => inst.id === firstId);
  const definition = instance ? getObjectDefinition(instance.assetId) : null;
  return definition?.editor.snap ?? 0.1;
}

export interface MapEditorOverlayProps {
  /** Optional game-supplied grouping for the scene-object sidebar. */
  roomResolver?: SceneObjectRoomResolver;
  /** Heading shown in the editor sidebar. Defaults to a neutral label. */
  builderTitle?: string;
}

export function MapEditorOverlay({ roomResolver, builderTitle = 'Runtime Builder' }: MapEditorOverlayProps = {}) {
  const enabled = useMapEditorStore((state) => state.enabled);
  const interactionMode = useMapEditorStore((state) => state.interactionMode);
  const snapMode = useMapEditorStore((state) => state.snapMode);
  const axisLock = useMapEditorStore((state) => state.axisLock);
  const searchQuery = useMapEditorStore((state) => state.searchQuery);
  const selectedObjectIds = useMapEditorStore((state) => state.selectedObjectIds);
  const pendingAssetId = useMapEditorStore((state) => state.pendingAssetId);
  const placementPreview = useMapEditorStore((state) => state.placementPreview);
  const placementRotationY = useMapEditorStore((state) => state.placementRotationY);
  const objectInstances = useMapEditorStore((state) => state.objectInstances);
  // Subscribed so the autosave debounce resets on every non-prop edit too.
  const markers = useMapEditorStore((state) => state.markers);
  const triggers = useMapEditorStore((state) => state.triggers);
  const lights = useMapEditorStore((state) => state.lights);
  const decals = useMapEditorStore((state) => state.decals);
  const saveState = useMapEditorStore((state) => state.saveState);
  const statusMessage = useMapEditorStore((state) => state.statusMessage);
  const undoStack = useMapEditorStore((state) => state.undoStack);
  const redoStack = useMapEditorStore((state) => state.redoStack);
  const autoSaveEnabled = useMapEditorStore((state) => state.autoSaveEnabled);
  const activeRightPanel = useMapEditorStore((state) => state.activeRightPanel);
  const firstSelectionTipDismissed = useMapEditorStore((state) => state.firstSelectionTipDismissed);
  const availableFloors = useMapEditorStore((state) => state.availableFloors);
  const gizmoMode = useMapEditorStore((state) => state.gizmoMode);
  const freeCamMode = useMapEditorStore((state) => state.freeCamMode);
  const marqueeRect = useMapEditorStore((state) => state.marqueeRect);
  const projectToScreen = useMapEditorStore((state) => state.projectToScreen);

  const setEnabled = useMapEditorStore((state) => state.setEnabled);
  const setInteractionMode = useMapEditorStore((state) => state.setInteractionMode);
  const setSnapMode = useMapEditorStore((state) => state.setSnapMode);
  const setAxisLock = useMapEditorStore((state) => state.setAxisLock);
  const setSearchQuery = useMapEditorStore((state) => state.setSearchQuery);
  const setPendingAssetId = useMapEditorStore((state) => state.setPendingAssetId);
  const rotatePlacementBy = useMapEditorStore((state) => state.rotatePlacementBy);
  const clearSelection = useMapEditorStore((state) => state.clearSelection);
  const selectAll = useMapEditorStore((state) => state.selectAll);
  const moveObject = useMapEditorStore((state) => state.moveObject);
  const rotateObject = useMapEditorStore((state) => state.rotateObject);
  const updateObjectParam = useMapEditorStore((state) => state.updateObjectParam);
  const deleteObject = useMapEditorStore((state) => state.deleteObject);
  const deleteSelected = useMapEditorStore((state) => state.deleteSelected);
  const duplicateObject = useMapEditorStore((state) => state.duplicateObject);
  const duplicateWithOffset = useMapEditorStore((state) => state.duplicateWithOffset);
  const nudgeSelected = useMapEditorStore((state) => state.nudgeSelected);
  const undo = useMapEditorStore((state) => state.undo);
  const redo = useMapEditorStore((state) => state.redo);
  const resetToSource = useMapEditorStore((state) => state.resetToSource);
  const restoreFromSnapshot = useMapEditorStore((state) => state.restoreFromSnapshot);
  const loadFromDisk = useMapEditorStore((state) => state.loadFromDisk);
  const saveToDisk = useMapEditorStore((state) => state.saveToDisk);
  const loadGitHeadSnapshot = useMapEditorStore((state) => state.loadGitHeadSnapshot);
  const setAutoSaveEnabled = useMapEditorStore((state) => state.setAutoSaveEnabled);
  const setActiveRightPanel = useMapEditorStore((state) => state.setActiveRightPanel);
  const dismissFirstSelectionTip = useMapEditorStore((state) => state.dismissFirstSelectionTip);
  const clearStatus = useMapEditorStore((state) => state.clearStatus);
  const validateAll = useMapEditorStore((state) => state.validateAll);
  const focusOnInstance = useMapEditorStore((state) => state.focusOnInstance);
  const copySelected = useMapEditorStore((state) => state.copySelected);
  const pasteAtPosition = useMapEditorStore((state) => state.pasteAtPosition);
  const setGizmoMode = useMapEditorStore((state) => state.setGizmoMode);
  const setFreeCamMode = useMapEditorStore((state) => state.setFreeCamMode);
  const setMarqueeRect = useMapEditorStore((state) => state.setMarqueeRect);

  const currentFloor = useEngineRuntimeStore((state) => state.currentFloor);
  const setCurrentFloor = useEngineRuntimeStore((state) => state.setCurrentFloor);
  const setDevFlyMode = useEngineRuntimeStore((state) => state.setDevFlyMode);
  const [collapsed, setCollapsed] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [confirmDeleteCount, setConfirmDeleteCount] = useState<number | null>(null);
  const [showDuplicatePopover, setShowDuplicatePopover] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [diffModalBefore, setDiffModalBefore] = useState<MapObjectInstance[]>([]);
  const [diffModalAfter, setDiffModalAfter] = useState<MapObjectInstance[]>([]);
  const [lintIssues, setLintIssues] = useState<LintIssue[]>([]);
  // Marquee drag tracking
  const marqueeStartRef = useRef<{ x: number; y: number } | null>(null);

  // Dirty across ALL collections (props + lights/triggers/decals/markers) so a
  // spawn-team or bomb-site-only edit still marks the document dirty.
  const isDirty = useMapEditorStore(isStoreDirty);

  const selectedObjectId = selectedObjectIds.length === 1 ? selectedObjectIds[0] : null;
  const selectedObject = useMemo(
    () => objectInstances.find((instance) => instance.id === selectedObjectId) ?? null,
    [objectInstances, selectedObjectId],
  );
  const selectedDefinition = selectedObject ? getObjectDefinition(selectedObject.assetId) : null;
  const pendingDefinition = pendingAssetId ? getObjectDefinition(pendingAssetId) : null;

  const editableParams = useMemo(() => {
    if (!selectedObject || !selectedDefinition) return [];
    const merged = {
      ...(selectedDefinition.defaultParams ?? {}),
      ...(selectedObject.params ?? {}),
    };
    return Object.entries(merged).filter(([, value]) => (
      typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean'
    ));
  }, [selectedDefinition, selectedObject]);

  const paramSchemas = useMemo(() => (
    selectedObject ? getParamSchemas(selectedObject.assetId) : {}
  ), [selectedObject]);

  const updateObject = useMapEditorStore((state) => state.updateObject);

  // Live per-field validation for the selected object (cheap; single instance).
  const issueByKey = useMemo(() => {
    const map: Record<string, ParamIssue> = {};
    if (!selectedObject) return map;
    for (const issue of validateInstance(selectedObject)) {
      if (!map[issue.key] || issue.severity === 'error') map[issue.key] = issue;
    }
    return map;
  }, [selectedObject]);

  // Debounced whole-document validation on mutation (drives lastValidationIssues).
  useEffect(() => {
    const handle = setTimeout(() => validateAll(), 300);
    return () => clearTimeout(handle);
  }, [objectInstances, validateAll]);

  // ── Delete with confirmation for >N ───────────────────────────────────────
  const deleteWithConfirm = useCallback(() => {
    const count = useMapEditorStore.getState().selectedObjectIds.length;
    if (count === 0) return;
    if (count > DELETE_CONFIRM_THRESHOLD) {
      setConfirmDeleteCount(count);
      return;
    }
    deleteSelected();
  }, [deleteSelected]);

  const openDiffModal = useCallback(() => {
    const state = useMapEditorStore.getState();
    setDiffModalBefore(state.sourceInstances);
    setDiffModalAfter(state.objectInstances);
    validateAll();
    const liveDoc = buildLiveMapDocument(state);
    if (liveDoc) {
      setLintIssues(lintMap(liveDoc, state.objectInstances));
    } else {
      setLintIssues([]);
    }
    setShowDiffModal(true);
  }, [validateAll]);

  const handleSaveConfirm = useCallback(async () => {
    await saveToDisk();
    setShowDiffModal(false);
  }, [saveToDisk]);

  const handleRevertInstance = useCallback((instance: MapObjectInstance | null, id: string) => {
    const state = useMapEditorStore.getState();
    let next: MapObjectInstance[];
    if (instance) {
      const existsInAfter = state.objectInstances.some((inst) => inst.id === id);
      if (existsInAfter) {
        next = state.objectInstances.map((inst) => (inst.id === id ? instance : inst));
      } else {
        next = [...state.objectInstances, instance];
      }
    } else {
      // Null means drop this id (used for "revert added")
      next = state.objectInstances.filter((inst) => inst.id !== id);
    }
    useMapEditorStore.setState({ objectInstances: next });
    setDiffModalAfter(next);
  }, []);

  // ── Shift key tracking for marquee ───────────────────────────────────────
  const [isShiftHeld, setIsShiftHeld] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftHeld(true);
    };
    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftHeld(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    const handleBlur = () => setIsShiftHeld(false);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const handleMarqueePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    marqueeStartRef.current = { x: e.clientX, y: e.clientY };
    setMarqueeRect({ x: e.clientX, y: e.clientY, w: 0, h: 0 });
  }, [setMarqueeRect]);

  const handleMarqueePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const start = marqueeStartRef.current;
    if (!start) return;
    const x = Math.min(start.x, e.clientX);
    const y = Math.min(start.y, e.clientY);
    const w = Math.abs(e.clientX - start.x);
    const h = Math.abs(e.clientY - start.y);
    setMarqueeRect({ x, y, w, h });
  }, [setMarqueeRect]);

  const handleMarqueePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture(e.pointerId);
    const start = marqueeStartRef.current;
    const rect = useMapEditorStore.getState().marqueeRect;
    marqueeStartRef.current = null;
    setMarqueeRect(null);
    if (!start || !rect) return;
    if (rect.w < 4 && rect.h < 4) return;

    const state = useMapEditorStore.getState();
    const projFn = state.projectToScreen;
    if (!projFn) return;

    const nextSelection: string[] = [];
    for (const inst of state.objectInstances) {
      if (inst.floor !== currentFloor) continue;
      const def = getObjectDefinition(inst.assetId);
      if (!def) continue;
      const screenPos = projFn(inst.transform.position);
      if (!screenPos) continue;
      const [sx, sy] = screenPos;
      if (
        sx >= rect.x &&
        sx <= rect.x + rect.w &&
        sy >= rect.y &&
        sy <= rect.y + rect.h
      ) {
        nextSelection.push(inst.id);
      }
    }
    useMapEditorStore.setState({ selectedObjectIds: nextSelection });
  }, [currentFloor, setMarqueeRect]);

  // ── Revert to git HEAD ────────────────────────────────────────────────────
  const revertToGitHead = useCallback(async () => {
    const instances = await loadGitHeadSnapshot();
    if (instances) {
      restoreFromSnapshot(instances, 'Restored from git HEAD');
    }
  }, [loadGitHeadSnapshot, restoreFromSnapshot]);

  // ── Auto-save ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!autoSaveEnabled || !isDirty) return;
    const timer = window.setTimeout(() => {
      void saveToDisk({ autosave: true });
    }, AUTO_SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [autoSaveEnabled, isDirty, objectInstances, markers, triggers, lights, decals, saveToDisk]);

  // ── Unsaved-changes title prefix + beforeunload ───────────────────────────
  useEffect(() => {
    const baseTitle = document.title.replace(/^•\s*/, '');
    document.title = isDirty ? `• ${baseTitle}` : baseTitle;
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = 'You have unsaved editor changes.';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.code === 'Tab' && !event.repeat) {
      event.preventDefault();
      setEnabled(!useMapEditorStore.getState().enabled);
      return;
    }

    if (!useMapEditorStore.getState().enabled) return;

    const state = useMapEditorStore.getState();
    const inInput = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';

    if (event.code === 'KeyZ' && (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.repeat) {
      event.preventDefault();
      undo();
      return;
    }
    if (((event.code === 'KeyZ' && event.shiftKey) || event.code === 'KeyY') && (event.ctrlKey || event.metaKey) && !event.repeat) {
      event.preventDefault();
      redo();
      return;
    }
    if (event.code === 'KeyS' && (event.ctrlKey || event.metaKey) && !event.repeat) {
      event.preventDefault();
      if (isStoreDirty(state)) {
        openDiffModal();
      } else {
        void saveToDisk();
      }
      return;
    }
    if (event.code === 'KeyA' && (event.ctrlKey || event.metaKey) && !event.repeat) {
      event.preventDefault();
      selectAll(useEngineRuntimeStore.getState().currentFloor as MapFloorLevel);
      return;
    }
    if (event.code === 'KeyD' && (event.ctrlKey || event.metaKey) && !event.repeat) {
      event.preventDefault();
      if (event.shiftKey) {
        if (state.selectedObjectIds.length === 1) {
          setShowDuplicatePopover(true);
        }
      } else if (state.selectedObjectIds.length === 1) {
        duplicateObject(state.selectedObjectIds[0]!);
      }
      return;
    }
    if ((event.code === 'Delete' || event.code === 'Backspace') && !event.repeat) {
      if (inInput) return;
      event.preventDefault();
      deleteWithConfirm();
      return;
    }

    if (event.code === 'KeyC' && (event.ctrlKey || event.metaKey) && !event.repeat) {
      if (inInput) return;
      event.preventDefault();
      copySelected();
      return;
    }
    if (event.code === 'KeyV' && (event.ctrlKey || event.metaKey) && !event.repeat) {
      if (inInput) return;
      event.preventDefault();
      const hit = useMapEditorStore.getState().lastRaycastHit;
      if (hit) {
        pasteAtPosition(hit[0], hit[1], currentFloor);
      }
      return;
    }

    if (inInput) return;

    // Axis-lock during drag (X / Z)
    if (event.code === 'KeyX' && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      setAxisLock(state.axisLock === 'x' ? null : 'x');
      return;
    }
    if (event.code === 'KeyC' && !event.ctrlKey && !event.metaKey) {
      // 'C' for constraint along Z - avoids conflict with Y-axis-up convention.
      event.preventDefault();
      setAxisLock(state.axisLock === 'z' ? null : 'z');
      return;
    }

    // Arrow-key nudging (shift = 10×)
    const arrowMap: Record<string, [number, number]> = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    if (arrowMap[event.code] && !event.ctrlKey && !event.metaKey) {
      if (state.selectedObjectIds.length === 0) return;
      event.preventDefault();
      const [dx, dz] = arrowMap[event.code]!;
      const step = getNudgeStep() * (event.shiftKey ? 10 : 1);
      nudgeSelected([dx * step, 0, dz * step]);
      return;
    }

    // Rotation (Q/E): rotates placement during placement, otherwise selected
    if (event.code === 'KeyQ' && !event.ctrlKey && !event.metaKey && !event.repeat) {
      if (state.interactionMode !== 'edit') return;
      event.preventDefault();
      if (state.pendingAssetId) {
        rotatePlacementBy(-Math.PI / 4);
      } else if (state.selectedObjectIds.length === 1) {
        rotateObject(state.selectedObjectIds[0]!, -Math.PI / 4);
      }
      return;
    }
    if (event.code === 'KeyE' && !event.ctrlKey && !event.metaKey && !event.repeat) {
      if (state.interactionMode !== 'edit') return;
      event.preventDefault();
      if (state.pendingAssetId) {
        rotatePlacementBy(Math.PI / 4);
      } else if (state.selectedObjectIds.length === 1) {
        rotateObject(state.selectedObjectIds[0]!, Math.PI / 4);
      }
      return;
    }

    // F: focus camera on the single selected object (Blender-style framing).
    if (event.code === 'KeyF' && !event.ctrlKey && !event.metaKey && !event.repeat) {
      if (state.interactionMode !== 'edit') return;
      if (state.selectedObjectIds.length !== 1) return;
      event.preventDefault();
      focusOnInstance(state.selectedObjectIds[0]!, {
        currentFloor: useEngineRuntimeStore.getState().currentFloor as MapFloorLevel,
      });
      return;
    }

    if (event.code === 'Escape' && !event.repeat) {
      if (state.pendingAssetId) {
        setPendingAssetId(null);
      } else if (state.axisLock) {
        setAxisLock(null);
      } else {
        clearSelection();
      }
      return;
    }

    if (event.code === 'Slash' && event.shiftKey && !event.repeat) {
      setShowShortcuts((v) => !v);
    }
  }, [
    clearSelection, copySelected, deleteWithConfirm, duplicateObject, focusOnInstance,
    nudgeSelected, openDiffModal, pasteAtPosition, redo, rotateObject, rotatePlacementBy,
    saveToDisk, selectAll, setAxisLock, setEnabled, setPendingAssetId, undo,
    currentFloor,
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // ── Mouse wheel rotates placement (with 45° angle-snap, Alt to bypass) ─────
  useEffect(() => {
    if (!enabled || interactionMode !== 'edit' || !pendingAssetId) return;
    const SNAP_STEP = Math.PI / 4;   // 45° - standard wall/door snap
    const FINE_STEP = Math.PI / 180; // 1° - Shift held
    const handler = (event: WheelEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('.editor-layer')) return;
      event.preventDefault();
      const { placementRotationY, snapMode } = useMapEditorStore.getState();
      if (event.altKey) {
        // Alt bypasses snap - raw continuous rotation
        const step = event.shiftKey ? FINE_STEP : SNAP_STEP;
        rotatePlacementBy(event.deltaY > 0 ? step : -step);
        return;
      }
      if (event.shiftKey) {
        // Shift = fine-tune
        rotatePlacementBy(event.deltaY > 0 ? FINE_STEP : -FINE_STEP);
        return;
      }
      // Default: snap to nearest 45° increment
      if (snapMode !== 'off') {
        const dir = event.deltaY > 0 ? 1 : -1;
        const snapped = Math.round((placementRotationY + dir * SNAP_STEP) / SNAP_STEP) * SNAP_STEP;
        const delta = snapped - placementRotationY;
        rotatePlacementBy(delta);
      } else {
        rotatePlacementBy(event.deltaY > 0 ? SNAP_STEP : -SNAP_STEP);
      }
    };
    window.addEventListener('wheel', handler, { passive: false });
    return () => window.removeEventListener('wheel', handler);
  }, [enabled, interactionMode, pendingAssetId, rotatePlacementBy]);

  // ── Escape clears first-time tip after dismiss ────────────────────────────
  const tipNeeded = !firstSelectionTipDismissed && enabled && interactionMode === 'edit' && selectedObjectIds.length === 0;
  const tipShownOnSelect = useRef(false);
  useEffect(() => {
    if (selectedObjectIds.length > 0) tipShownOnSelect.current = true;
  }, [selectedObjectIds.length]);

  useEffect(() => {
    if (!statusMessage) return;
    const timer = window.setTimeout(() => clearStatus(), 2600);
    return () => window.clearTimeout(timer);
  }, [clearStatus, statusMessage]);

  if (!enabled) {
    return (
      <div className="editor-toggle-hint ui-panel">
        <span className="editor-toggle-kbd">Tab</span>
        <span>Map Edit Mode</span>
      </div>
    );
  }

  return (
    <div className="editor-layer">
      {/* ── Marquee backdrop handler ── */}
      {interactionMode === 'edit' ? (
        <div
          className="editor-marquee-backdrop"
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: isShiftHeld ? 'auto' : 'none',
            cursor: 'crosshair',
          }}
          onPointerDown={handleMarqueePointerDown}
          onPointerMove={handleMarqueePointerMove}
          onPointerUp={handleMarqueePointerUp}
        />
      ) : null}

      {/* ── Marquee selection box ── */}
      {marqueeRect ? (
        <div
          className="editor-marquee-select"
          style={{
            position: 'fixed',
            left: marqueeRect.x,
            top: marqueeRect.y,
            width: marqueeRect.w,
            height: marqueeRect.h,
            border: '1px dashed #7fe3ff',
            background: 'rgba(127, 227, 255, 0.15)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        />
      ) : null}

      {/* ── Alignment Guides ── */}
      {interactionMode === 'edit' && selectedObject && projectToScreen ? (
        <AlignmentGuides
          selectedObject={selectedObject}
          objectInstances={objectInstances}
          projectToScreen={projectToScreen}
        />
      ) : null}

      <ThumbnailRenderer />

      {/* ── Left sidebar ─────────────────────────────────────────────────── */}
      <aside className={`editor-sidebar ui-panel${collapsed ? ' is-collapsed' : ''}`}>
        <header className="editor-shell-header">
          <div>
            <span className="editor-shell-eyebrow">Map Editor</span>
            <h2>{builderTitle}</h2>
          </div>
          <div className="editor-shell-actions">
            <button type="button" className="editor-chip" onClick={() => setShowShortcuts((v) => !v)} title="Keyboard shortcuts (?)">
              ?
            </button>
            <button type="button" className="editor-chip" onClick={() => setCollapsed(!collapsed)}>
              {collapsed ? 'Expand' : 'Collapse'}
            </button>
            <button type="button" className="editor-chip" onClick={() => setEnabled(false)}>
              Close
            </button>
          </div>
        </header>

        {!collapsed ? (
          <>
            <div className="editor-toolbar">
              <button
                type="button"
                className={`editor-mode-btn${interactionMode === 'walk' ? ' is-active' : ''}`}
                onClick={() => setInteractionMode('walk')}
              >
                Walk
              </button>
              <button
                type="button"
                className={`editor-mode-btn${interactionMode === 'edit' ? ' is-active' : ''}`}
                onClick={() => setInteractionMode('edit')}
              >
                Edit
              </button>
              <button
                type="button"
                className={`editor-mode-btn${snapMode === 'grid' ? ' is-active' : ''}`}
                onClick={() => setSnapMode(snapMode === 'grid' ? 'off' : 'grid')}
                title="Toggle grid snapping (Shift+wheel=1°, default=45°, Alt=bypass)"
              >
                Snap
              </button>
              <button
                type="button"
                className={`editor-mode-btn${freeCamMode ? ' is-active' : ''}`}
                title="Free-cam mode: WASD + Space/Shift to fly"
                onClick={() => {
                  const next = !freeCamMode;
                  setFreeCamMode(next);
                  setDevFlyMode(next);
                }}
              >
                Fly
              </button>
            </div>

            {/* ── Floor selector ─────────────────────────────────────────── */}
            {availableFloors.length > 0 ? (
              <div className="editor-floor-switcher" role="group" aria-label="Floor selector">
                <span className="editor-floor-label">Floor</span>
                {availableFloors.slice().sort((a, b) => b - a).map((fl) => (
                  <button
                    key={fl}
                    type="button"
                    className={`editor-floor-btn${currentFloor === fl ? ' is-active' : ''}`}
                    onClick={() => setCurrentFloor(fl)}
                    aria-pressed={currentFloor === fl}
                    title={`Switch to floor ${fl}`}
                  >
                    {fl === 0 ? 'G' : fl > 0 ? `+${fl}` : `${fl}`}
                  </button>
                ))}
              </div>
            ) : null}

            {/* ── Gizmo mode ─────────────────────────────────────────────── */}
            {interactionMode === 'edit' && selectedObjectIds.length > 0 ? (
              <div className="editor-toolbar editor-toolbar-gizmo">
                {(['translate', 'rotate', 'scale', 'off'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={`editor-mode-btn${gizmoMode === mode ? ' is-active' : ''}`}
                    onClick={() => setGizmoMode(mode)}
                    title={`Gizmo: ${mode}`}
                  >
                    {mode === 'translate' ? '↔' : mode === 'rotate' ? '↻' : mode === 'scale' ? '⤡' : '·'}
                    {' '}{mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="editor-status-row">
              <span className="editor-status-pill">Floor {currentFloor}</span>
              {selectedObjectIds.length > 1 ? (
                <span className="editor-status-pill is-accent">{selectedObjectIds.length} selected</span>
              ) : null}
              {pendingDefinition ? (
                <span className="editor-status-pill is-accent">
                  Placing {pendingDefinition.name}
                  {placementRotationY !== 0
                    ? ` · ${Math.round((placementRotationY * 180) / Math.PI)}°`
                    : ''}
                </span>
              ) : null}
              {axisLock ? (
                <span className="editor-status-pill is-warning">Axis: {axisLock.toUpperCase()}</span>
              ) : null}
              {placementPreview ? (
                <span className="editor-status-pill">
                  {formatNumber(placementPreview.position[0])}, {formatNumber(placementPreview.position[2])}
                </span>
              ) : null}
              {isDirty ? (
                <span className="editor-status-pill is-dirty">Unsaved</span>
              ) : null}
            </div>

            <div className="editor-toolbar editor-toolbar-secondary">
              <button
                type="button"
                className="editor-action-btn"
                onClick={undo}
                disabled={undoStack.length === 0}
                title="Undo (Ctrl+Z)"
              >
                ↩ Undo{undoStack.length > 0 ? ` (${undoStack.length})` : ''}
              </button>
              <button
                type="button"
                className="editor-action-btn"
                onClick={redo}
                disabled={redoStack.length === 0}
                title="Redo (Ctrl+Shift+Z)"
              >
                ↪ Redo{redoStack.length > 0 ? ` (${redoStack.length})` : ''}
              </button>
            </div>

            <div className="editor-toolbar editor-toolbar-secondary">
              <button type="button" className="editor-action-btn" onClick={() => void loadFromDisk()} title="Reload the map from disk, discarding any unsaved changes">
                Load
              </button>
              <button type="button" className="editor-action-btn" onClick={resetToSource} disabled={!isDirty} title="Revert unsaved changes back to the last-loaded version">
                Revert
              </button>
              <button
                type="button"
                className="editor-action-btn"
                onClick={() => void revertToGitHead()}
                title="Restore from last committed version"
              >
                HEAD
              </button>
              <button
                type="button"
                className={`editor-action-btn is-primary${isDirty ? ' has-dirty-dot' : ''}`}
                onClick={() => {
                  if (isDirty) openDiffModal();
                  else void saveToDisk();
                }}
                disabled={saveState === 'saving'}
                title="Save (Ctrl+S)"
              >
                {saveState === 'saving' ? 'Saving…' : 'Save'}
              </button>
            </div>

            <div className="editor-toolbar editor-toolbar-secondary">
              <label className="editor-checkbox">
                <input
                  type="checkbox"
                  checked={autoSaveEnabled}
                  onChange={(event) => setAutoSaveEnabled(event.target.checked)}
                />
                <span>Auto-save (10s → .autosave.bak)</span>
              </label>
            </div>

            <ObjectBrowser
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              selectedAssetId={pendingAssetId}
              onSelectAsset={(assetId) => setPendingAssetId(assetId)}
            />
          </>
        ) : null}
      </aside>

      {/* ── Right panel: Inspector ↔ Scene list ↔ Prefabs ─────────────────── */}
      <section className="editor-inspector ui-panel">
        <div className="editor-right-tabs">
          <button
            type="button"
            data-testid="editor-tab-inspector"
            className={`editor-right-tab${activeRightPanel === 'inspector' ? ' is-active' : ''}`}
            onClick={() => setActiveRightPanel('inspector')}
          >
            Inspector
          </button>
          <button
            type="button"
            data-testid="editor-tab-list"
            className={`editor-right-tab${activeRightPanel === 'list' ? ' is-active' : ''}`}
            onClick={() => setActiveRightPanel('list')}
          >
            Scene list
          </button>
          <button
            type="button"
            data-testid="editor-tab-prefabs"
            className={`editor-right-tab${activeRightPanel === 'prefabs' ? ' is-active' : ''}`}
            onClick={() => setActiveRightPanel('prefabs')}
          >
            Prefabs
          </button>
        </div>

        {activeRightPanel === 'list' ? (
          <ObjectListSidebar floor={currentFloor as MapFloorLevel} roomResolver={roomResolver} />
        ) : activeRightPanel === 'prefabs' ? (
          <PrefabLibrary currentFloor={currentFloor} />
        ) : (
          <>
            <header className="editor-section-header">
              <div>
                <span className="editor-section-eyebrow">Inspector</span>
                <h3>
                  {selectedObjectIds.length > 1
                    ? `${selectedObjectIds.length} objects`
                    : selectedDefinition?.name ?? 'No selection'}
                </h3>
              </div>
              {selectedObjectIds.length > 0 ? (
                <button type="button" className="editor-chip" onClick={clearSelection}>
                  Deselect
                </button>
              ) : null}
            </header>

            {selectedObjectIds.length > 1 ? (
              <div className="editor-inspector-body">
                <div className="editor-inspector-copy">
                  <p>{selectedObjectIds.length} objects selected. Drag any of them to move all. Arrow keys nudge.</p>
                </div>
                <button type="button" className="editor-delete-btn" onClick={deleteWithConfirm}>
                  Delete All Selected
                </button>
              </div>
            ) : selectedObject && selectedDefinition ? (
              <div className="editor-inspector-body">
                <div className="editor-inspector-copy">
                  <span>{selectedObject.id}</span>
                  <p>{selectedDefinition.description}</p>
                </div>

                <label className="editor-field">
                  <span>Name</span>
                  <input
                    className="param-name-input"
                    placeholder={selectedDefinition.name}
                    value={selectedObject.name ?? ''}
                    onChange={(event) => {
                      const next = event.target.value;
                      updateObject(
                        selectedObject.id,
                        (instance) => ({ ...instance, name: next.trim() === '' ? undefined : next }),
                        'Rename object',
                      );
                    }}
                  />
                </label>

                <div className="editor-field-grid">
                  <label className="editor-field">
                    <span>X</span>
                    <input
                      value={formatNumber(selectedObject.transform.position[0])}
                      onChange={(event) => moveObject(selectedObject.id, [
                        parseFieldNumber(event.target.value, selectedObject.transform.position[0]),
                        selectedObject.transform.position[1],
                        selectedObject.transform.position[2],
                      ])}
                    />
                  </label>
                  <label className="editor-field">
                    <span>Z</span>
                    <input
                      value={formatNumber(selectedObject.transform.position[2])}
                      onChange={(event) => moveObject(selectedObject.id, [
                        selectedObject.transform.position[0],
                        selectedObject.transform.position[1],
                        parseFieldNumber(event.target.value, selectedObject.transform.position[2]),
                      ])}
                    />
                  </label>
                </div>

                <div className="editor-toolbar editor-toolbar-secondary">
                  <button type="button" className="editor-action-btn" onClick={() => rotateObject(selectedObject.id, -Math.PI / 4)} title="Rotate -45° (Q)">
                    ↺ −45°
                  </button>
                  <button type="button" className="editor-action-btn" onClick={() => rotateObject(selectedObject.id, Math.PI / 4)} title="Rotate +45° (E)">
                    ↻ +45°
                  </button>
                  <button type="button" className="editor-action-btn" onClick={() => rotateObject(selectedObject.id, -Math.PI / 2)}>
                    −90°
                  </button>
                  <button type="button" className="editor-action-btn" onClick={() => rotateObject(selectedObject.id, Math.PI / 2)}>
                    +90°
                  </button>
                </div>

                <div className="editor-toolbar editor-toolbar-secondary">
                  <button
                    type="button"
                    className="editor-action-btn"
                    onClick={() => duplicateObject(selectedObject.id)}
                    title="Duplicate (Ctrl+D)"
                  >
                    ⧉ Duplicate
                  </button>
                  <button
                    type="button"
                    className="editor-action-btn"
                    onClick={() => setShowDuplicatePopover(true)}
                    title="Duplicate with offset pattern (Ctrl+Shift+D)"
                  >
                    ⧉⧉ Pattern…
                  </button>
                  <button
                    type="button"
                    className="editor-action-btn"
                    onClick={() => focusOnInstance(selectedObject.id, { currentFloor: currentFloor as MapFloorLevel })}
                    title="Frame camera on this object (F)"
                  >
                    ⌖ Focus
                  </button>
                </div>

                {editableParams.length > 0 ? (
                  <div className="editor-params">
                    {editableParams.map(([key, value]) => {
                      const schema = paramSchemas[key];
                      if (typeof value === 'boolean' || schema?.type === 'boolean') {
                        return (
                          <label key={key} className="editor-field">
                            <span>{key}</span>
                            <button
                              type="button"
                              className={`editor-toggle-btn${value ? ' is-active' : ''}`}
                              onClick={() => updateObjectParam(selectedObject.id, key, !value)}
                            >
                              {value ? 'Enabled' : 'Disabled'}
                            </button>
                          </label>
                        );
                      }
                      if (schema?.type === 'color' || (typeof value === 'string' && /^#[0-9a-f]{3,8}$/i.test(value))) {
                        return (
                          <label key={key} className="editor-field">
                            <span>{key}</span>
                            <input
                              type="color"
                              value={typeof value === 'string' ? value : '#ffffff'}
                              onChange={(event) => updateObjectParam(selectedObject.id, key, event.target.value)}
                            />
                          </label>
                        );
                      }
                      if (schema?.type === 'number' || typeof value === 'number') {
                        const isNumber = typeof value === 'number';
                        const numSchema = schema?.type === 'number' ? schema : null;
                        const step = numSchema?.step ?? (numSchema?.integer ? 1 : 0.1);
                        const hasRange =
                          numSchema != null &&
                          typeof numSchema.min === 'number' &&
                          typeof numSchema.max === 'number';
                        const setNumber = (raw: string) => {
                          // Ignore a transiently-empty field so clearing to retype
                          // doesn't snap the value to 0 (Number('') === 0).
                          if (raw.trim() === '') return;
                          const nextValue = isNumber ? parseFieldNumber(raw, value as number) : raw;
                          updateObjectParam(selectedObject.id, key, nextValue);
                        };
                        return (
                          <label key={key} className="editor-field">
                            <span>{key}</span>
                            <div className="param-field-row">
                              {hasRange ? (
                                <input
                                  type="range"
                                  min={numSchema!.min}
                                  max={numSchema!.max}
                                  step={step}
                                  value={String(value)}
                                  onChange={(event) => setNumber(event.target.value)}
                                />
                              ) : null}
                              <input
                                className="param-field-num"
                                type="number"
                                min={numSchema?.min}
                                max={numSchema?.max}
                                step={step}
                                value={String(value)}
                                onChange={(event) => setNumber(event.target.value)}
                              />
                            </div>
                          </label>
                        );
                      }
                      if (schema?.type === 'enum') {
                        return (
                          <label key={key} className="editor-field">
                            <span>{key}</span>
                            <select
                              value={String(value)}
                              onChange={(event) => updateObjectParam(selectedObject.id, key, event.target.value)}
                            >
                              {schema.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          </label>
                        );
                      }
                      return (
                        <label key={key} className="editor-field">
                          <span>{key}</span>
                          <input
                            value={String(value)}
                            onChange={(event) => updateObjectParam(selectedObject.id, key, event.target.value)}
                          />
                        </label>
                      );
                    })}
                  </div>
                ) : null}

                {Object.keys(issueByKey).length > 0 ? (
                  <div className="param-issues">
                    {Object.values(issueByKey).map((issue) => (
                      <p
                        key={issue.key}
                        className={`param-field-issue${issue.severity === 'warning' ? ' is-warning' : ''}`}
                      >
                        <strong>{issue.key}</strong>: {issue.message}
                      </p>
                    ))}
                  </div>
                ) : null}

                <button
                  type="button"
                  className="editor-delete-btn"
                  onClick={() => deleteObject(selectedObject.id)}
                  title="Delete (Del)"
                >
                  Delete Object
                </button>
              </div>
            ) : (
              <div className="editor-empty-state">
                <p>Select an existing prop in edit mode, or choose an asset from the library and click a floor panel to place it.</p>
                <p>Walk mode keeps pointer lock movement intact. Edit mode releases the cursor for direct scene manipulation.</p>
              </div>
            )}
          </>
        )}
      </section>

      {/* ── First-time selection tip ─────────────────────────────────────── */}
      {tipNeeded && !tipShownOnSelect.current ? (
        <div className="editor-firsttime-tip ui-panel">
          <strong>Edit mode is active.</strong>
          <p>
            Drag objects to move them · <kbd>Q</kbd>/<kbd>E</kbd> to rotate · <kbd>Del</kbd> to remove · arrow keys to nudge.
            Hold <kbd>X</kbd> or <kbd>C</kbd> during drag to lock an axis.
          </p>
          <button type="button" className="editor-chip" onClick={dismissFirstSelectionTip}>
            Got it
          </button>
        </div>
      ) : null}

      {/* ── Keyboard shortcuts overlay ───────────────────────────────────── */}
      {showShortcuts ? (
        <div className="editor-shortcuts-overlay ui-panel">
          <header className="editor-section-header">
            <div>
              <span className="editor-section-eyebrow">Shortcuts</span>
              <h3>Keyboard Reference</h3>
            </div>
            <button type="button" className="editor-chip" onClick={() => setShowShortcuts(false)}>✕</button>
          </header>
          <div className="editor-shortcuts-grid">
            {[
              ['Tab', 'Toggle editor'],
              ['Ctrl+Z', 'Undo'],
              ['Ctrl+Shift+Z / Ctrl+Y', 'Redo'],
              ['Ctrl+S', 'Save (with diff preview)'],
              ['Ctrl+A', 'Select all (floor)'],
              ['Ctrl+D', 'Duplicate selected'],
              ['Ctrl+Shift+D', 'Duplicate with pattern…'],
              ['Arrow keys', 'Nudge selected (Shift = 10×)'],
              ['X', 'Lock to X axis during drag'],
              ['C', 'Lock to Z axis during drag'],
              ['Delete', 'Delete selected (confirm > 3)'],
              ['Q / E', 'Rotate −45° / +45° (selected or ghost)'],
              ['Mouse wheel', 'Rotate ghost during placement (Shift = 1°)'],
              ['Right-click', 'Cancel placement'],
              ['F', 'Frame camera on selected object'],
              ['Escape', 'Clear axis lock / deselect / cancel'],
              ['?', 'Toggle this panel'],
            ].map(([key, label]) => (
              <div key={key} className="editor-shortcut-row">
                <kbd className="editor-shortcut-kbd">{key}</kbd>
                <span className="editor-shortcut-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ── Confirm delete modal ─────────────────────────────────────────── */}
      {confirmDeleteCount !== null ? (
        <ConfirmDeleteModal
          count={confirmDeleteCount}
          onCancel={() => setConfirmDeleteCount(null)}
          onConfirm={() => {
            deleteSelected();
            setConfirmDeleteCount(null);
          }}
        />
      ) : null}

      {/* ── Duplicate offset popover ─────────────────────────────────────── */}
      {showDuplicatePopover && selectedObject ? (
        <DuplicateOffsetPopover
          onCancel={() => setShowDuplicatePopover(false)}
          onConfirm={(count, dx, dz) => {
            duplicateWithOffset(selectedObject.id, count, [dx, dz]);
            setShowDuplicatePopover(false);
          }}
        />
      ) : null}

      {/* ── Pre-save diff modal ──────────────────────────────────────────── */}
      {showDiffModal ? (
        <PreSaveDiffModal
          before={diffModalBefore}
          after={diffModalAfter}
          validationIssues={useMapEditorStore.getState().lastValidationIssues}
          lintIssues={lintIssues}
          isSaving={saveState === 'saving'}
          onCancel={() => setShowDiffModal(false)}
          onConfirm={() => void handleSaveConfirm()}
          onRevertInstance={handleRevertInstance}
        />
      ) : null}

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {statusMessage ? (
        <div className={`editor-toast ui-panel is-${saveState}`}>
          {statusMessage}
        </div>
      ) : null}
    </div>
  );
}
